import React, { useState } from "react";
import { settingsApi } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { Sliders } from "lucide-react";

const Row = ({ label, val, unit = "", children, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div data-testid={testid + "-value"} className="font-mono text-sm text-emerald-300">{val}{unit}</div>
    </div>
    {children}
  </div>
);

export default function AutoTraderSettings({ settings, onSaved }) {
  const [s, setS] = useState(settings);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    try {
      setBusy(true);
      const updated = await settingsApi.update(s);
      onSaved?.(updated);
      toast.success("Auto-trader parameters saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="aq-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Hands-off</div>
          <div className="font-display text-lg font-bold mt-0.5 flex items-center gap-2"><Sliders className="w-4 h-4"/> Auto-trader</div>
        </div>
      </div>
      <div className="space-y-4">
        <Row label="Risk tolerance" val={s.risk_tolerance} unit="/10" testid={TID.settingsRisk}>
          <Slider data-testid={TID.settingsRisk} value={[s.risk_tolerance]} min={1} max={10} step={1}
            onValueChange={(v) => setS({ ...s, risk_tolerance: v[0] })} />
        </Row>
        <Row label="Position size" val={s.position_size_pct} unit="%" testid={TID.settingsPositionSize}>
          <Slider data-testid={TID.settingsPositionSize} value={[s.position_size_pct]} min={1} max={25} step={1}
            onValueChange={(v) => setS({ ...s, position_size_pct: v[0] })} />
        </Row>
        <Row label="Stop loss (tiny)" val={s.stop_loss_pct} unit="%" testid={TID.settingsStopLoss}>
          <Slider data-testid={TID.settingsStopLoss} value={[s.stop_loss_pct]} min={0.2} max={5} step={0.1}
            onValueChange={(v) => setS({ ...s, stop_loss_pct: v[0] })} />
        </Row>
        <Row label="Take profit (tiny)" val={s.take_profit_pct} unit="%" testid={TID.settingsTakeProfit}>
          <Slider data-testid={TID.settingsTakeProfit} value={[s.take_profit_pct]} min={0.2} max={5} step={0.1}
            onValueChange={(v) => setS({ ...s, take_profit_pct: v[0] })} />
        </Row>
        <Row label="Confidence threshold" val={s.confidence_threshold} unit="%" testid={TID.settingsConfidence}>
          <Slider data-testid={TID.settingsConfidence} value={[s.confidence_threshold]} min={60} max={99} step={1}
            onValueChange={(v) => setS({ ...s, confidence_threshold: v[0] })} />
        </Row>
      </div>
      <Button data-testid={TID.settingsSave} onClick={save} disabled={busy}
        className="mt-5 w-full h-10 rounded-md bg-indigo-500 hover:bg-indigo-400 text-white font-semibold">
        {busy ? "Saving…" : "Save parameters"}
      </Button>
    </div>
  );
}
